import type { Investment, InvestmentId, ItemId } from '@finsim/model';
import { createEvent, createStore, merge, sample } from 'effector';
import { $project, commandIssued, editCommitted } from '../../entities/project';
import { investmentSelected, viewChanged } from '../../entities/view-settings';
import { newId } from '../../shared/lib/id';

/**
 * "Analyse returns": opens the Returns view on an investment, adding one first when needed.
 * - From an asset (`itemId`): the investment the asset belongs to, or a new one named after it,
 *   scoped to tagged items with the asset tagged (rent, a mortgage, … can be added in the view).
 * - Otherwise: the current investment, or a new one covering the whole variant (a project that
 *   holds only the deal needs nothing else).
 */
export const returnsAnalysisRequested = createEvent<{ name: string; itemId?: ItemId }>();

type Request = { investment: Investment; items: ItemId[] } | { open: InvestmentId | null };

const planned = sample({
  clock: returnsAnalysisRequested,
  source: $project,
  filter: Boolean,
  fn: (project, { name, itemId }): Request => {
    const item = itemId === undefined ? undefined : project.items[itemId];
    if (item?.investment !== undefined) return { open: item.investment };
    if (item) {
      const investment = { id: newId('inv') as InvestmentId, name, scope: 'items' as const };
      return { investment, items: [item.id] };
    }
    if (project.investments.length > 0) return { open: null };
    return { investment: { id: newId('inv') as InvestmentId, name, scope: 'variant' }, items: [] };
  },
});

// A new investment: the view opens once it is in the project (the Returns view shows only then),
// which is after the command has been applied.
const added = planned.filterMap((r) => ('investment' in r ? r : undefined));
const $adding = createStore<InvestmentId | null>(null).on(
  added,
  (_, { investment }) => investment.id,
);
sample({
  clock: added,
  fn: ({ investment, items }) => ({
    command: { type: 'investment/add' as const, investment, items },
  }),
  target: commandIssued,
});

const opened = merge([
  planned.filterMap((r) => ('open' in r ? r.open : undefined)),
  sample({
    clock: editCommitted,
    source: $adding,
    filter: (id, { command }) => command.type === 'investment/add' && command.investment.id === id,
    fn: (id) => id,
  }),
]);
$adding.reset(opened);
sample({
  clock: opened.filterMap((id) => id ?? undefined),
  target: investmentSelected,
});
sample({ clock: opened, fn: () => 'returns' as const, target: viewChanged });
